import React, { useState, useEffect, useRef } from 'react';
import { fetchWithCorpId, asArray } from '../utils/api';
import { Section, Field, FormLoading } from './ui/formkit';
import { useToast } from './ui/Toast';
import { toDateInputValue } from '../utils/dateUtils';

// The parties and cycle are fixed once an order exists; the backend rejects
// changes (vms-backend#121), so the form does not offer them on edit.
const FIXED_FIELD_HINT = 'Fixed after creation. To change it, end this order and create a new one.';

const DEFAULT_ORDER = {
  id: '', clientId: '', employeeId: '',
  billRate: '', invoiceDueDays: '', itemDescription: '',
  invoiceFrequency: '', startDate: '', endDate: '',
  firstPeriodEndDate: '', billingCode: '',
  status: 'Pending', notes: '', timesheetRequired: false,
};

function OrderForm({
  mode = 'create',
  initialData = null,
  orderId,
  formRef,
  hideActions = false,
  onSuccess,
  onCancel,
}) {
  const toast = useToast();
  const isEdit = mode === 'edit';
  const [formData, setFormData] = useState(DEFAULT_ORDER);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [message, setMessage] = useState('');
  const [codeEdited, setCodeEdited] = useState(mode === 'edit');
  const [loadingRecord, setLoadingRecord] = useState(isEdit && Boolean(orderId));
  const [saving, setSaving] = useState(false);
  // Snapshot of the last saved state; drives the drawer's discard guard.
  // In edit mode it is set once the record loads.
  const initialSnapshotRef = useRef(isEdit ? null : JSON.stringify(DEFAULT_ORDER));

  // Apply initialData when passed (legacy route path).
  useEffect(() => {
    if (initialData) {
      const normalized = {
        ...DEFAULT_ORDER,
        ...initialData,
        startDate: toDateInputValue(initialData.startDate),
        endDate: toDateInputValue(initialData.endDate),
        firstPeriodEndDate: toDateInputValue(initialData.firstPeriodEndDate),
      };
      setFormData(normalized);
      initialSnapshotRef.current = JSON.stringify(normalized);
    }
  }, [initialData]);

  // Drawer path: fetch by orderId.
  useEffect(() => {
    if (!isEdit || !orderId) {
      setLoadingRecord(false);
      return;
    }
    let cancelled = false;
    setLoadingRecord(true);
    fetchWithCorpId(`/order/${orderId}`)
      .then((data) => {
        if (cancelled || !data) return;
        const normalized = {
          ...DEFAULT_ORDER,
          ...data,
          startDate: toDateInputValue(data.startDate),
          endDate: toDateInputValue(data.endDate),
          firstPeriodEndDate: toDateInputValue(data.firstPeriodEndDate),
        };
        setFormData(normalized);
        initialSnapshotRef.current = JSON.stringify(normalized);
      })
      .catch((error) => {
        if (!cancelled) setMessage('Could not load this order: ' + error.message);
      })
      .finally(() => {
        if (!cancelled) setLoadingRecord(false);
      });
    return () => { cancelled = true; };
  }, [isEdit, orderId]);

  // Pickers.
  useEffect(() => {
    Promise.all([
      fetchWithCorpId('/clients?page=0&size=99999&sort=name'),
      fetchWithCorpId('/employees?page=0&size=99999&sort=fullName'),
    ]).then(([v, e]) => {
      setClients(asArray(v).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setEmployees(asArray(e).slice().sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '')));
    }).catch(() => {});
  }, []);

  const isDirty = initialSnapshotRef.current !== null
    && JSON.stringify(formData) !== initialSnapshotRef.current;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (message) setMessage('');
    // Typing in the code field stops the auto-suggestion; clearing it resumes.
    if (name === 'billingCode') setCodeEdited(value.trim() !== '');
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Suggest a code from client + employee + start year until the user types their own.
  useEffect(() => {
    if (mode !== 'create' || codeEdited) return;
    const client = clients.find(v => String(v.id) === String(formData.clientId));
    const employee = employees.find(e => String(e.id) === String(formData.employeeId));
    if (!client || !employee) return;
    const compact = (s) => (s || '').toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 10);
    const year = formData.startDate ? formData.startDate.slice(0, 4) : String(new Date().getFullYear());
    const suggestion = [compact(client.name), compact(employee.fullName), year].filter(Boolean).join('-');
    setFormData(prev => (prev.billingCode === suggestion ? prev : { ...prev, billingCode: suggestion }));
  }, [mode, codeEdited, formData.clientId, formData.employeeId, formData.startDate, clients, employees]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage('');
    setSaving(true);
    try {
      const id = formData.id || orderId || (initialData && initialData.id);
      const url = isEdit && id ? `/order/${id}` : '/order';
      const method = isEdit ? 'PUT' : 'POST';
      const dataToSubmit = { ...formData, corpId: localStorage.getItem('selectedCorpId') || '0' };
      await fetchWithCorpId(url, { method, body: JSON.stringify(dataToSubmit) });
      initialSnapshotRef.current = JSON.stringify(formData);
      toast.success(isEdit ? 'Order updated' : 'Order created');
      onSuccess?.();
    } catch (error) {
      setMessage(error.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingRecord) {
    return <FormLoading label="Loading order…" />;
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} data-dirty={isDirty ? 'true' : 'false'}>
      {message && (
        <div className="alert error" role="alert" style={{ marginBottom: '20px' }}>{message}</div>
      )}

      <Section title="Order" description="Identifier and the people involved.">
        <Field label="Status" required>
          <select name="status" value={formData.status} onChange={handleChange} required>
            <option value="Pending">Pending</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </Field>
        <Field label="Employee" required hint={isEdit ? FIXED_FIELD_HINT : undefined}>
          <select name="employeeId" value={formData.employeeId} onChange={handleChange} required disabled={isEdit}>
            <option value="">Select employee</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.fullName}</option>)}
          </select>
        </Field>
        <Field label="Client" required hint={isEdit ? FIXED_FIELD_HINT : undefined}>
          <select name="clientId" value={formData.clientId} onChange={handleChange} required disabled={isEdit}>
            <option value="">Select client</option>
            {clients.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
      </Section>

      <Section title="Rate & cycle" description="What you charge and how often.">
        <Field label="Bill rate" required>
          <input type="number" step="0.01" name="billRate" value={formData.billRate} onChange={handleChange} required placeholder="0.00" />
        </Field>
        <Field label="Frequency" required hint={isEdit ? FIXED_FIELD_HINT : undefined}>
          <select name="invoiceFrequency" value={formData.invoiceFrequency} onChange={handleChange} required disabled={isEdit}>
            <option value="">Select frequency</option>
            <option value="WEEKLY">Weekly</option>
            <option value="BIWEEKLY">Biweekly</option>
            <option value="MONTHLY">Monthly</option>
          </select>
        </Field>
        <Field label="Invoice due days" required hint="Days until an issued invoice is due.">
          <input type="number" name="invoiceDueDays" value={formData.invoiceDueDays} onChange={handleChange} required />
        </Field>
        <Field label="Billing code" required>
          <input type="text" name="billingCode" value={formData.billingCode} onChange={handleChange} required
            placeholder="Suggested from client and employee" />
        </Field>
        <Field label="Item description" span={2} required>
          <textarea name="itemDescription" value={formData.itemDescription} onChange={handleChange} rows={3} required />
        </Field>
      </Section>

      <Section title="Dates">
        <Field label="Start date" required>
          <input type="date" name="startDate" value={formData.startDate} onChange={handleChange} required />
        </Field>
        <Field label="First period end" required>
          <input type="date" name="firstPeriodEndDate" value={formData.firstPeriodEndDate} onChange={handleChange} required />
        </Field>
        <Field label="End date">
          <input type="date" name="endDate" value={formData.endDate} onChange={handleChange} />
        </Field>
        <Field label="Timesheet required">
          <select name="timesheetRequired" value={formData.timesheetRequired ? 'true' : 'false'} onChange={handleChange}>
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </Field>
      </Section>

      <Section title="Notes" description="Optional context for this order." grid="1fr">
        <Field label="Notes">
          <textarea name="notes" value={formData.notes} onChange={handleChange} rows={4} placeholder="Internal notes" />
        </Field>
      </Section>

      {!hideActions && (
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>Cancel</button>
          )}
        </div>
      )}
    </form>
  );
}

export default OrderForm;
